if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        if (this.dataset.mediaLayout !== 'quickview') {
          this.init();
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this));
        }
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.imageRatio = this.dataset.imageRatio || 'adapt';
        this.setupDeferredMediaObserver();
        this.setSliderOptions();

        this.mobileMediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        // The vertical thumbnail rail changes direction at 900px, which is a
        // different breakpoint from the theme's 768px mobile query. Swiper can
        // retain its old direction and inline slide heights when that boundary
        // is crossed, so rebuild the gallery on either layout transition.
        this.verticalThumbnailsMediaQuery = window.matchMedia('(min-width: 900px)');
        this.boundViewportLayoutChange = this.scheduleViewportLayoutRefresh.bind(this);
        this.mobileMediaQuery.addEventListener('change', this.boundViewportLayoutChange);
        this.verticalThumbnailsMediaQuery.addEventListener('change', this.boundViewportLayoutChange);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          if (window.requestIdleCallback) {
            window.requestIdleCallback(() => this.initImageZoom(), { timeout: 2000 });
          } else {
            setTimeout(() => this.initImageZoom(), 500);
          }
        }

        this.initialized = true;
        // Ensure gallery is always revealed (gallery-ready added by initSlider when using Swiper;
        // for desktop grid/no-slider layouts we add it here).
        if (!this.classList.contains('gallery-ready')) {
          this.classList.add('gallery-ready');
        }
      }

      disconnectedCallback() {
        this.mobileMediaQuery?.removeEventListener('change', this.boundViewportLayoutChange);
        this.verticalThumbnailsMediaQuery?.removeEventListener('change', this.boundViewportLayoutChange);

        if (this.viewportLayoutFrame) {
          cancelAnimationFrame(this.viewportLayoutFrame);
          this.viewportLayoutFrame = null;
        }

        this.deferredMediaObserver?.disconnect?.();
        this.destroySlider();
        this.lightbox?.destroy?.();
        this.lightbox = null;
        this.initialized = false;
      }

      scheduleViewportLayoutRefresh() {
        if (this.viewportLayoutFrame) cancelAnimationFrame(this.viewportLayoutFrame);

        this.viewportLayoutFrame = requestAnimationFrame(() => {
          this.viewportLayoutFrame = null;
          this.refreshSliderForViewport();
        });
      }

      refreshSliderForViewport() {
        const activeIndex = this.sliderInstance?.slider?.activeIndex || 0;

        this.destroySlider();
        this.setSliderOptions();
        this.updateMediaLayout();

        const slider = this.sliderInstance?.slider;
        if (slider && activeIndex > 0 && activeIndex < slider.slides.length) {
          slider.slideTo(activeIndex, 0, false);
          this.thumbsInstance?.slider?.slideTo(activeIndex, 0, false);
          this.syncOverlayDots(activeIndex);
        }
      }

      isMobileViewport() {
        return this.mobileMediaQuery?.matches ?? FoxTheme.config.mqlMobile;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        const isForceAspect = this.imageRatio !== 'adapt';

        this.sliderOptions = {
          init: false,
          slidesPerView: isForceAspect ? 1 : (this.enableMobileThumbnails ? 1 : 'auto'),
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: !isForceAspect,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        const thumbnailSlides = this.elements.thumbnails ? this.elements.thumbnails.querySelectorAll('.product__thumbs-item') : [];
        const thumbnailCount = thumbnailSlides.length || 4;

        this.thumbsOptions = {
          slidesPerView: Math.min(5, thumbnailCount),
          centerInsufficientSlides: false,
          watchOverflow: true,
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: false,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            390: {
              slidesPerView: Math.min(6, thumbnailCount),
            },
            600: {
              slidesPerView: Math.min(8, thumbnailCount),
            },
            768: {
              slidesPerView: Math.min(8, thumbnailCount),
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'horizontal',
                slidesPerView: Math.min(8, thumbnailCount),
                spaceBetween: mediaItemGap,
              },
              900: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: Math.min(10, thumbnailCount),
              },
              1536: {
                slidesPerView: Math.min(12, thumbnailCount),
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (this.isMobileViewport()) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          // Mobile + no thumbnails: use dots instead of fraction, hide arrows
          if (this.isMobileViewport() && !this.enableMobileThumbnails) {
            this.sliderOptions.pagination = false;
            this.sliderOptions.navigation = false;
            this.sliderOptions.slidesPerView = 'auto';
          } else if (this.isMobileViewport() && this.enableMobileThumbnails) {
            // Mobile + thumbnails: no pagination at all
            this.sliderOptions.pagination = false;
            this.sliderOptions.navigation = false;
          }

          // Determine correct initial slide index from Liquid server-side data.
          // We do NOT try to set initialSlide in sliderOptions because FoxTheme.Carousel
          // may reset it. Instead we slideTo() synchronously right after slider.init().
          let correctInitialSlide = 0;
          const liquidInitialSlide = parseInt(this.dataset.initialSlide, 10);
          if (!isNaN(liquidInitialSlide) && liquidInitialSlide > 0) {
            correctInitialSlide = liquidInitialSlide;
          }

          // FALLBACK: JS-set pendingInitialMediaId (set before init() call)
          const pendingMediaId = this.dataset.pendingInitialMediaId;
          if (pendingMediaId) {
            const allSlides = this.querySelectorAll('.product__media-item');
            allSlides.forEach((slide, i) => {
              if (String(slide.dataset.mediaId) === String(pendingMediaId)) {
                correctInitialSlide = i;
              }
            });
            delete this.dataset.pendingInitialMediaId;
          }

          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();

          // Synchronously jump to the correct variant slide BEFORE the browser paints.
          // The gallery is CSS-hidden (opacity:0) so this jump is invisible to the user.
          // runCallbacks=true ensures activeIndex, pagination dots, and all internal
          // Swiper state is fully updated — otherwise swiping starts from the wrong position.
          if (correctInitialSlide > 0) {
            const slider = this.sliderInstance.slider;
            slider.slideTo(correctInitialSlide, 0, true);
            slider.update(); // force Swiper to recalculate positions, activeIndex, pagination
            if (slider.thumbs && slider.thumbs.swiper) {
              slider.thumbs.swiper.slideTo(correctInitialSlide, 0, true);
            }
            this.syncOverlayDots(correctInitialSlide);
          }

          // Reveal the gallery now that it's at the correct position.
          // CSS has `media-gallery:not(.gallery-ready)` with opacity:0 to prevent
          // the flash of slide-0 (White) before this point.
          this.classList.add('gallery-ready');
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object' && !this.sliderInstance.slider.destroyed) {
          this.sliderInstance.slider.destroy(true, true);
        }
        this.sliderInstance = false;

        // The thumbs Swiper is a separate Carousel instance. Leaving it alive
        // preserves `swiper-vertical` and very large inline heights after a
        // desktop-to-mobile resize, creating a resize/update feedback loop.
        if (typeof this.thumbsInstance === 'object' && !this.thumbsInstance.slider.destroyed) {
          this.thumbsInstance.slider.destroy(true, true);
        }
        this.thumbsInstance = false;
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight,
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox?.pswp?.currIndex) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            // The initial/selected slide is already rendered eager by Liquid.
            // Do not preload adjacent full-size images during the LCP window.
            slides[activeIndex]
              .querySelectorAll('[data-deferred-media-image]')
              .forEach((image) => this.hydrateDeferredMedia(image));
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          // Sync custom overlay dots and bind click handlers (mobile, no thumbs)
          this.syncOverlayDots(activeIndex);
          if (!this.dotsClickBound) {
            this.dotsClickBound = true;
            const dotsContainer = this.querySelector('.product__media-overlay-dots');
            if (dotsContainer) {
              dotsContainer.addEventListener('click', (e) => {
                const dot = e.target.closest('.overlay-dot');
                if (!dot || !this.sliderInstance?.slider) return;
                e.stopPropagation();
                const index = parseInt(dot.dataset.index, 10);
                if (!isNaN(index)) {
                  this.sliderInstance.slider.slideTo(index);
                }
              });
            }
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.hydrateSlideMedia(slides, activeIndex);
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          // Sync custom overlay dots
          this.syncOverlayDots(activeIndex);
        });
      }

      syncOverlayDots(activeIndex) {
        const overlay = this.querySelector('.product__media-overlay-mobile');
        if (!overlay) return;
        const dots = overlay.querySelectorAll('.overlay-dot');
        dots.forEach((dot, i) => {
          dot.classList.toggle('overlay-dot--active', i === activeIndex);
        });
      }

      setupDeferredMediaObserver() {
        this.deferredMediaObserver?.disconnect?.();
        this.deferredMediaObserver = null;

        if (!('IntersectionObserver' in window)) {
          this.querySelectorAll('[data-deferred-media-image]').forEach((image) => this.hydrateDeferredMedia(image));
          return;
        }

        this.deferredMediaObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.hydrateDeferredMedia(entry.target);
              this.deferredMediaObserver.unobserve(entry.target);
            });
          },
          {
            rootMargin: '300px 0px',
            threshold: 0.25,
          }
        );

        this.querySelectorAll('[data-deferred-media-image]').forEach((image) => {
          this.deferredMediaObserver.observe(image);
        });
      }

      hydrateSlideMedia(slides, activeIndex) {
        [activeIndex - 1, activeIndex, activeIndex + 1].forEach((index) => {
          const slide = slides[index];
          if (!slide) return;
          slide.querySelectorAll('[data-deferred-media-image]').forEach((image) => this.hydrateDeferredMedia(image));
        });
      }

      hydrateDeferredMedia(image) {
        if (!image || image.dataset.deferredLoaded === 'true') return;

        if (image.dataset.src) {
          image.setAttribute('src', image.dataset.src);
        }

        if (image.dataset.srcset) {
          image.setAttribute('srcset', image.dataset.srcset);
        }

        if (image.dataset.sizes) {
          image.setAttribute('sizes', image.dataset.sizes);
        }

        image.dataset.deferredLoaded = 'true';
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant, instant = false) {
        if (!variant) return;

        // Get the target media ID from the variant — this is the most reliable identifier
        const mediaId = (variant.featured_media && variant.featured_media.id) ||
                        (variant.featured_image && variant.featured_image.id);

        if (this.sliderInstance && this.sliderInstance.slider) {
          const slider = this.sliderInstance.slider;
          let slideIndex = 0;

          // PRIMARY: match by data-media-id (set in Liquid, matches Shopify's media.id)
          if (mediaId) {
            for (let i = 0; i < slider.slides.length; i++) {
              if (String(slider.slides[i].dataset.mediaId) === String(mediaId)) {
                slideIndex = i;
                break;
              }
            }
          } else if (variant.featured_image && variant.featured_image.src) {
            // FALLBACK: filename matching when no mediaId available
            const srcUrl = variant.featured_image.src.split('?')[0];
            const filename = srcUrl.split('/').pop().replace(/\.[^/.]+$/, '').replace(/_[0-9x]+$/, '');
            for (let i = 0; i < slider.slides.length; i++) {
              const slide = slider.slides[i];
              const img = slide.querySelector('img');
              const dataSrc = slide.getAttribute('data-src') || (img && img.getAttribute('data-src')) || '';
              const imgSrc = img && img.src ? img.src.split('?')[0] : '';
              if ((imgSrc && imgSrc.includes(filename)) || (dataSrc && dataSrc.includes(filename))) {
                slideIndex = i;
                break;
              }
            }
          }

          const currentIndex = slider.activeIndex;
          if (currentIndex === slideIndex) {
            if (slider.thumbs && slider.thumbs.swiper) {
              slider.thumbs.swiper.slideTo(slideIndex, instant ? 0 : 300, true);
            }
            this.syncOverlayDots(slideIndex);
            return;
          }
          slider.slideTo(slideIndex, instant ? 0 : 300, true);

          if (slider.thumbs && slider.thumbs.swiper) {
            slider.thumbs.swiper.slideTo(slideIndex);
          }
          this.syncOverlayDots(slideIndex);

        } else {
          // No Swiper (desktop grid or mobile pre-init): reorder DOM
          let featuredMedia = variant.featured_media;
          if (!featuredMedia && variant.featured_image) {
            featuredMedia = {
              id: variant.featured_image.id,
              position: variant.featured_image.position || 0,
              media_type: 'image',
            };
          }
          if (!featuredMedia) featuredMedia = { position: 1, media_type: 'image' };
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Restore original order first
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        // Find the featured media and move it to front
        const featuredIndex = newMedias.findIndex((m) => String(m.dataset.mediaId) === String(featuredMedia.id));
        if (featuredIndex <= 0) {
          // Already first, but we still must rewrite DOM in case a previous sort left it wrong
          if (featuredIndex < 0) return; // media not found at all
        } else {
          const [element] = newMedias.splice(featuredIndex, 1);
          newMedias.unshift(element);
        }

        // Always re-render DOM so previous sort state is corrected
        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Scroll the viewer container back to start so the featured image is visible
        requestAnimationFrame(() => {
          const viewer = this.elements.viewer;
          if (viewer) { viewer.scrollLeft = 0; viewer.scrollTop = 0; }
          const wrap = this.querySelector('.product__media-gallery-viewer-wrap');
          if (wrap) wrap.scrollLeft = 0;
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            requestAnimationFrame(() => {
              window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
            });
          }
        }
      }
    }
  );
}
